import clsx from "clsx";
import { useState } from "react";

type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick"
> & {
  variant?: "primary" | "secondary";
  size?: "sm" | "md" | "lg";
  asyncLoader?: boolean;
  loading?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
};

const baseStyles =
  "font-bold font-mono transition-all duration-200 transform hover:scale-105 active:scale-95 rounded-bl-md rounded-tr-md " +
  "aria-selected:border-gray-300";

const variants = {
  primary:
    "bg-gradient-to-b from-gray-700 to-gray-900 text-white border-2 border-gray-500 hover:shadow-xl hover:from-gray-600 hover:to-gray-800",
  secondary:
    "bg-gradient-to-b from-gray-800 to-gray-950 text-gray-300 border-2 border-gray-600 hover:shadow-xl hover:from-gray-700 hover:to-gray-900",
};

const sizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-6 py-3 text-lg",
};

export function Button({
  children,
  className = "",
  variant = "primary",
  size = "md",
  onClick,
  loading = false,
  asyncLoader = false,
  ...props
}: ButtonProps) {
  const [asyncLoading, setAsyncLoading] = useState(false);

  return (
    <button
      className={clsx(baseStyles, variants[variant], sizes[size], className)}
      // TODO: implement aria-busy ui
      aria-busy={loading || asyncLoading}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={async (e) => {
        if (asyncLoader) {
          try {
            setAsyncLoading(true);
            await onClick?.(e);
          } finally {
            setAsyncLoading(false);
          }
        } else {
          void onClick?.(e);
        }
      }}
      {...props}
    >
      <span className="relative">{children}</span>
    </button>
  );
}
